import { MAP_IDS, MAP_NAMES, RANDOM_MAP, WAR_MAP_IDS, type AuthoredMapId, type MapId } from '@cratered/shared';
import catalogJson from './catalog.json';
import { LEVELS, type LevelDef } from './levels';
import { decodeSpec, familyName, generateLevel, type Family, type MapSpec } from './mapgen';
import { BUILDING_MAPS, BUILDING_MAP_IDS, buildingLevel, buildingTags, isBuildingMap } from './buildingmaps';
import { TUNNEL_MAPS, TUNNEL_MAP_IDS, isTunnelMap, tunnelLevel, tunnelTags } from './tunnelmaps';
import { WAR_MAPS, isWarMap, warLevel, warMapDef, warTags } from './warmaps';

/**
 * Every playable map by id: the authored arenas, the catalogue of bot-tested generated maps
 * (catalog.json, written by scripts/mapgen-eval.ts) and inline `gen:` specs. Generated levels are
 * built once and cached; the same id always gives the same map on every client.
 */

export interface CatalogStats {
  /** Mean turns (full rounds) a fight lasted in bot testing. */
  rounds: number;
  /** Coefficient of variation of that length: lower is more repeatable. */
  cv: number;
  drawRate: number;
  /** |left win rate − 0.5|. */
  bias: number;
  matches: number;
  score: number;
}

export interface CatalogEntry {
  id: string;
  name: string;
  family: Family;
  spec: MapSpec;
  /** Set on the ten inspired variants of each winner. */
  parent?: string;
  blurb?: string;
  stats?: CatalogStats;
}

export interface Catalog {
  generated: string;
  maps: CatalogEntry[];
}

export const CATALOG: Catalog = catalogJson as unknown as Catalog;
const BY_ID = new Map(CATALOG.maps.map((m) => [m.id, m]));

/** Parents first, each followed by its variants: the lobby's grouped picker. */
export const CATALOG_GROUPS: { label: string; maps: { id: string; name: string }[] }[] = CATALOG.maps
  .filter((m) => !m.parent)
  .map((p) => ({
    label: `${p.name} (${familyName(p.family)})`,
    maps: [{ id: p.id, name: p.name }, ...CATALOG.maps.filter((m) => m.parent === p.id).map((m) => ({ id: m.id, name: m.name }))],
  }));

export function isAuthoredMap(id: MapId): id is AuthoredMapId {
  return (MAP_IDS as readonly string[]).includes(id);
}

/** Every map a match can be played on, in picker order: the arenas, the war maps, the tunnel maps, then the catalogue. */
export const PLAYABLE_MAP_IDS: readonly string[] = [...MAP_IDS, ...WAR_MAP_IDS, ...TUNNEL_MAP_IDS, ...BUILDING_MAP_IDS, ...CATALOG.maps.map((m) => m.id)];

export interface MapCategory {
  id: 'raftwars' | 'war' | 'tunnels' | 'buildings' | 'generated';
  label: string;
  blurb: string;
  maps: { id: string; name: string; tags: string[] }[];
}

/** The picker's categories: what a random pick can be narrowed to, with tags for finer filters. */
export const MAP_CATEGORIES: readonly MapCategory[] = [
  {
    id: 'raftwars', label: 'Raft Wars', blurb: 'Water arenas: rafts, boats, piers and islands.',
    maps: MAP_IDS.map((id) => ({ id, name: MAP_NAMES[id], tags: ['water', ...(LEVELS[id].catapults?.length ? ['catapults'] : [])] })),
  },
  {
    id: 'war', label: 'Territory War', blurb: 'Land arenas laid out after the Territory War stages.',
    maps: WAR_MAPS.map((m) => ({ id: m.id, name: m.name, tags: warTags(m) })),
  },
  {
    id: 'tunnels', label: 'Tunnels', blurb: 'Underground arenas: warrens, mines and catacombs.',
    maps: TUNNEL_MAPS.map((m) => ({ id: m.id, name: m.name, tags: tunnelTags() })),
  },
  {
    id: 'buildings', label: 'Buildings', blurb: 'Three buildings of three floors each: fight room to room.',
    maps: BUILDING_MAPS.map((m) => ({ id: m.id, name: m.name, tags: buildingTags() })),
  },
  {
    id: 'generated', label: 'Generated', blurb: 'Bot-tested maps from the generator, in families.',
    maps: CATALOG.maps.map((m) => ({ id: m.id, name: m.name, tags: ['generated', m.family] })),
  },
];

/** The ids a lobby pool should hold for a set of categories (and, when given, only these tags). */
export function poolFor(categories: readonly MapCategory['id'][], tags?: readonly string[]): string[] {
  return MAP_CATEGORIES.filter((c) => categories.includes(c.id))
    .flatMap((c) => c.maps.filter((m) => !tags || tags.length === 0 || m.tags.some((t) => tags.includes(t))).map((m) => m.id));
}

const cache = new Map<string, LevelDef>();

/**
 * Levels built in the level worker and handed over. A war or generated level can take seconds to build, so
 * the next map is built in the background while a round is played; resolveLevel then hands out this one
 * instead of building the same level again on the thread that draws the game.
 */
const prebuilt = new Map<string, LevelDef>();
/** Ids this thread has resolved. Their level object is out in the world by now and has to stay the one it is. */
const resolved = new Set<string>();

/** Takes a level that was built elsewhere. Refused when this thread has built that level itself, or when it is not that level. */
export function primeLevel(id: string, level: LevelDef): boolean {
  if (resolved.has(id) || prebuilt.has(id) || isAuthoredMap(id)) return false;
  if (!level || level.id !== id || !Array.isArray(level.shapes) || typeof level.width !== 'number' || typeof level.height !== 'number') return false;
  prebuilt.set(id, level);
  return true;
}

/** Whether resolveLevel(id) is quick right now: an arena, a cave or a building map (nothing to build), or a level already built or handed over. */
export function isLevelReady(id: string): boolean {
  return isAuthoredMap(id) || isTunnelMap(id) || isBuildingMap(id) || resolved.has(id) || prebuilt.has(id);
}

/** Whether an id names a map at all, without building it. */
export function isPlayableMap(id: string): boolean {
  return isAuthoredMap(id) || isWarMap(id) || isTunnelMap(id) || isBuildingMap(id) || BY_ID.has(id) || decodeSpec(id) !== null;
}

function buildLevel(id: MapId): LevelDef {
  if (isWarMap(id)) return warLevel(id) ?? LEVELS[MAP_IDS[0]];
  if (isTunnelMap(id)) return tunnelLevel(id) ?? LEVELS[MAP_IDS[0]];
  if (isBuildingMap(id)) return buildingLevel(id) ?? LEVELS[MAP_IDS[0]];
  const hit = cache.get(id);
  if (hit) return hit;
  let level: LevelDef | null = null;
  const entry = BY_ID.get(id);
  if (entry) level = generateLevel(entry.spec, { id, name: entry.name, blurb: entry.blurb });
  else {
    const spec = decodeSpec(id);
    if (spec) level = generateLevel(spec, { id, name: `${familyName(spec.family)} #${spec.seed % 1000}` });
  }
  if (!level) return LEVELS[MAP_IDS[0]];
  cache.set(id, level);
  return level;
}

export function resolveLevel(id: MapId): LevelDef {
  if (isAuthoredMap(id)) return LEVELS[id];
  const level = prebuilt.get(id) ?? buildLevel(id);
  resolved.add(id);
  return level;
}

export function mapName(id: MapId): string {
  if (id === RANDOM_MAP) return 'Random';
  if (isAuthoredMap(id)) return MAP_NAMES[id];
  const war = warMapDef(id);
  if (war) return war.name;
  return BY_ID.get(id)?.name ?? resolveLevel(id).name;
}

export function mapBlurb(id: MapId): string {
  if (id === RANDOM_MAP) return 'A map is drawn from the pool for every round of the match.';
  // Read it from the definition where there is one: building a level only to show its blurb (the vote shows three) can take seconds.
  const war = warMapDef(id);
  if (war) return war.blurb;
  const entry = BY_ID.get(id);
  if (entry?.blurb) return entry.blurb;
  return resolveLevel(id).blurb;
}

/** A catalogue map id other than `not`, or the first arena when the catalogue is empty. */
export function randomCatalogMap(not?: string, rng: () => number = Math.random): string {
  const pool = CATALOG.maps.filter((m) => m.id !== not);
  if (pool.length === 0) return MAP_IDS[0];
  return pool[Math.floor(rng() * pool.length)]!.id;
}
